import SoftBadge from "@/components/atoms/SoftBadge";
import AreYouSurToContinueModal from "@/components/molecules/modals/AreYouSurToContinueModal";
import AllCompanyCourseStatusesHeader from "@/components/molecules/table-headers/AllCompanyCourseStatusesHeader";
import Table from "@/components/molecules/table/Table";
import { splitText } from "@/helpers/utils";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useTableState } from "@/hooks/useTableState";
import CompanyCourseStatus from "@/interfaces/CompanyCourseStatus";
import { IDndChange } from "@/interfaces/Table";
import {
  useDeleteCompanyCourseStatusMutation,
  useGetCompanyCourseStatusesQuery,
  useUpdateCompanyCourseStatusMutation,
} from "@/services/companies/courses/statuses";

import { faEdit, faEye, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { ColumnDef, TableState } from "@tanstack/react-table";
import { DateTime } from "luxon";
import { ElementRef, FC, useRef } from "react";
import { Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import { ToastContentProps, toast } from "react-toastify";

const CompanyCourseStatusesView: FC = () => {
  const DeleteModalRef =
    useRef<ElementRef<typeof AreYouSurToContinueModal>>(null);
  const [deleteCompanyCourseStatusAsync] =
    useDeleteCompanyCourseStatusMutation();
  const [updateCompanyCourseStatusAsync] =
    useUpdateCompanyCourseStatusMutation();

  const [state, setState] = useTableState<Partial<TableState>>(
    "companyCourseStatuses",
    {
      pagination: {
        pageIndex: 0,
        pageSize: 15,
      },
      globalFilter: "",
    },
  );

  const debouncedQuery = useDebouncedValue(state.globalFilter, 500);

  const { data } = useGetCompanyCourseStatusesQuery({
    ...state.pagination,
    query: debouncedQuery,
    ...(state.sorting?.[0] && {
      sortBy: state.sorting[0].id,
      sortDirection: state.sorting[0].desc ? "desc" : "asc",
    }),
  });

  const changeOrderAsync = async ({ fromId, toId }: IDndChange) => {
    await toast.promise(
      updateCompanyCourseStatusAsync([fromId, { toId }]).unwrap,
      {
        pending: "Changement de position en cours...",
        success: {
          render() {
            return "Changement de position confirmé 🤩";
          },
        },
        error: {
          render({}: ToastContentProps<FetchBaseQueryError>) {
            return "Une erreur est survenue";
          },
        },
      }
    );
  };

  const onDelete = async (id: string) => {
    toast.promise(deleteCompanyCourseStatusAsync(id).unwrap(), {
      pending: "Suppression du statut en cours ...",
      success: {
        render() {
          DeleteModalRef.current?.showOrHiddenModal();
          return (
            <p className="mb-0 text-center">Le statut à bien été supprimé 🤩</p>
          );
        },
      },
      error: {
        render({ data }: ToastContentProps<FetchBaseQueryError>) {
          return "Une erreur est survenue";
        },
      },
    });
  };

  const columns = useRef<ColumnDef<CompanyCourseStatus>[]>([
    {
      header: "Ordre",
      enableSorting: false,
      footer: (props) => props.column.id,
      id: "order",
      cell: (info) => info.getValue(),
    },
    {
      header: "Actions",
      enableSorting: false,
      footer: (props) => props.column.id,
      accessorFn: ({ id }) => (
        <>
          <Link
            to={`/company-course-statuses/${id}`}
            className="btn btn-primary btn-sm"
          >
            <FontAwesomeIcon icon={faEye} />
          </Link>
          <Link
            to={`/company-course-statuses/${id}/edit`}
            className="btn btn-info btn-sm ms-2"
          >
            <FontAwesomeIcon icon={faEdit} />
          </Link>
          <Button
            variant="danger"
            size="sm"
            className="ms-2"
            onClick={() =>
              DeleteModalRef.current?.showOrHiddenModal(id.toString())
            }
          >
            <FontAwesomeIcon icon={faTrash} />
          </Button>
        </>
      ),
      id: "actions",
      cell: (info) => info.getValue(),
    },
    {
      header: "#",
      footer: (props) => props.column.id,
      accessorFn: ({ id }) => splitText(id, 10),
      id: "id",
      cell: (info) => info.getValue(),
    },
    {
      header: "Nom",
      footer: (props) => props.column.id,
      accessorFn: ({ name, backgroundColor, textColor }) => (
        <SoftBadge bg={backgroundColor} textColor={textColor}>
          {name}
        </SoftBadge>
      ),
      id: "name",
      cell: (info) => info.getValue(),
    },
    {
      header: "Crée le",
      footer: (props) => props.column.id,
      accessorFn: (row) =>
        DateTime.fromISO(row.createdAt).toFormat("dd-MM-yyyy"),
      id: "createdAt",
      cell: (info) => info.getValue(),
    },
  ]).current;

  return (
    <div className="formalys-page formalys-list-page">
      <AreYouSurToContinueModal
        ref={DeleteModalRef}
        onContinue={onDelete}
        title="Souhaitez-vous vraiment supprimé le statut de la entreprise qui a pour id : {{id}} ?"
      />
      <Table
        resource="company-course-statuses"
        tableName="companyCourseStatuses"
        HeaderComponent={AllCompanyCourseStatusesHeader}
        onDndChange={changeOrderAsync}
        columns={columns}
        state={state}
        onStateChange={setState}
        data={data?.data}
        meta={data?.meta}
      />
    </div>
  );
};

export default CompanyCourseStatusesView;
